import React from 'react';
import { Layers, Database, Server, Monitor, Shield, Map as MapIcon, HardDrive, Cpu } from 'lucide-react';

export default function SystemArchitecture() {
  return (
    <div className="card" style={{ maxWidth: '1000px', margin: '0 auto', padding: '2rem' }}>
      <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
        <h1 style={{ color: '#1e293b', fontSize: '2.5rem', fontWeight: 800, marginBottom: '0.5rem' }}>System Architecture</h1>
        <p style={{ color: '#64748b', fontSize: '1.1rem' }}>
          Overview of Toyota Material Handling - Sales Area & Simulator Platform
        </p>
      </div>

      {/* High-Level Architecture Diagram using Flex/Grid */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem', marginBottom: '4rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
          
          {/* Frontend */}
          <div style={{ backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '12px', padding: '1.5rem', textAlign: 'center' }}>
            <Monitor size={48} color="#2563eb" style={{ marginBottom: '1rem' }} />
            <h3 style={{ color: '#1e40af', marginBottom: '0.5rem' }}>Frontend (Client)</h3>
            <p style={{ fontSize: '0.9rem', color: '#3b82f6', marginBottom: '1rem' }}>React 18 + Vite</p>
            <ul style={{ textAlign: 'left', fontSize: '0.85rem', color: '#1e3a8a', paddingLeft: '1.5rem', lineHeight: 1.6 }}>
              <li>React Router DOM for SPA routing</li>
              <li>Lucide React for iconography</li>
              <li>Vis.gl for Google Maps Integration</li>
              <li>Vanilla CSS for styling</li>
            </ul>
          </div>

          {/* Backend */}
          <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px', padding: '1.5rem', textAlign: 'center' }}>
            <Server size={48} color="#16a34a" style={{ marginBottom: '1rem' }} />
            <h3 style={{ color: '#166534', marginBottom: '0.5rem' }}>Backend (API Server)</h3>
            <p style={{ fontSize: '0.9rem', color: '#22c55e', marginBottom: '1rem' }}>Go + Fiber Framework</p>
            <ul style={{ textAlign: 'left', fontSize: '0.85rem', color: '#14532d', paddingLeft: '1.5rem', lineHeight: 1.6 }}>
              <li>RESTful API Endpoints</li>
              <li>JWT Authentication (stateless)</li>
              <li>Database Connection Pooling</li>
              <li>High concurrency & low latency</li>
            </ul>
          </div>

          {/* Database */}
          <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '12px', padding: '1.5rem', textAlign: 'center' }}>
            <Database size={48} color="#dc2626" style={{ marginBottom: '1rem' }} />
            <h3 style={{ color: '#991b1b', marginBottom: '0.5rem' }}>Database</h3>
            <p style={{ fontSize: '0.9rem', color: '#ef4444', marginBottom: '1rem' }}>PostgreSQL 15</p>
            <ul style={{ textAlign: 'left', fontSize: '0.85rem', color: '#7f1d1d', paddingLeft: '1.5rem', lineHeight: 1.6 }}>
              <li>Persistent data storage</li>
              <li>Relational schemas for Users, Teams, Customers, Leads, AGV Profiles</li>
              <li>Docker Volume for data persistence</li>
            </ul>
          </div>

        </div>

        {/* Microservices & External APIs */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
          
          <div style={{ backgroundColor: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: '12px', padding: '1.5rem', textAlign: 'center' }}>
            <Cpu size={40} color="#7c3aed" style={{ marginBottom: '1rem' }} />
            <h3 style={{ color: '#5b21b6', marginBottom: '0.5rem' }}>DWG Converter Service</h3>
            <p style={{ fontSize: '0.85rem', color: '#4c1d95', lineHeight: 1.6 }}>Node.js microservice running in an isolated container to parse AutoCAD (.dwg) blueprints into SVG/DXF format for the Forklift Simulator.</p>
          </div>

          <div style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '12px', padding: '1.5rem', textAlign: 'center' }}>
            <MapIcon size={40} color="#d97706" style={{ marginBottom: '1rem' }} />
            <h3 style={{ color: '#92400e', marginBottom: '0.5rem' }}>Google Maps Platform</h3>
            <p style={{ fontSize: '0.85rem', color: '#78350f', lineHeight: 1.6 }}>External API for rendering sales territories, clustering customer pins, and executing background batch geocoding.</p>
          </div>

        </div>
      </div>

      <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '3rem 0' }} />

      <h2 style={{ color: '#1e293b', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <Layers size={24} /> System Features & Modules
      </h2>
      
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1rem' }}>
        <div style={{ padding: '1.5rem', backgroundColor: '#f8fafc', borderRadius: '8px', borderLeft: '4px solid #3b82f6' }}>
          <h4 style={{ color: '#0f172a', marginBottom: '0.5rem', fontSize: '1.1rem' }}>1. Customer & Sales Territory Management</h4>
          <p style={{ color: '#475569', fontSize: '0.9rem', lineHeight: 1.6, margin: 0 }}>
            Core functionality to import customer data via Excel, automatically geocode addresses to map pins, and draw polygon territories to assign geographical boundaries to specific sales teams.
          </p>
        </div>

        <div style={{ padding: '1.5rem', backgroundColor: '#f8fafc', borderRadius: '8px', borderLeft: '4px solid #f59e0b' }}>
          <h4 style={{ color: '#0f172a', marginBottom: '0.5rem', fontSize: '1.1rem' }}>2. Lead Distribution System</h4>
          <p style={{ color: '#475569', fontSize: '0.9rem', lineHeight: 1.6, margin: 0 }}>
            Captures leads and uses spatial intersection algorithms (Point-in-Polygon) to automatically assign leads to the correct sales team based on the lead's location and the drawn territories. Trigger email notifications.
          </p>
        </div>

        <div style={{ padding: '1.5rem', backgroundColor: '#f8fafc', borderRadius: '8px', borderLeft: '4px solid #10b981' }}>
          <h4 style={{ color: '#0f172a', marginBottom: '0.5rem', fontSize: '1.1rem' }}>3. Forklift Simulator (AGV)</h4>
          <p style={{ color: '#475569', fontSize: '0.9rem', lineHeight: 1.6, margin: 0 }}>
            An interactive 3D/2D simulation environment. Allows uploading of factory floorplans (.dwg files), managing AGV profiles, drawing paths, and simulating automated vehicle routes.
          </p>
        </div>

        <div style={{ padding: '1.5rem', backgroundColor: '#f8fafc', borderRadius: '8px', borderLeft: '4px solid #ef4444' }}>
          <h4 style={{ color: '#0f172a', marginBottom: '0.5rem', fontSize: '1.1rem' }}>4. Highly Secure System Administration</h4>
          <p style={{ color: '#475569', fontSize: '0.9rem', lineHeight: 1.6, margin: 0 }}>
            Restricted routes based on user roles and specific Google Emails. Includes a low-level Database Viewer with secondary master password protection for real-time table inspection and debugging.
          </p>
        </div>
      </div>

      <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '3rem 0' }} />

      <h2 style={{ color: '#1e293b', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <HardDrive size={24} /> Infrastructure & Deployment
      </h2>
      <div style={{ padding: '1.5rem', backgroundColor: '#1e293b', borderRadius: '8px', color: '#f8fafc' }}>
        <p style={{ marginBottom: '1rem', fontSize: '0.95rem', color: '#cbd5e1' }}>
          The entire application is containerized using Docker, ensuring that the development, staging, and production environments are identical.
        </p>
        <div style={{ backgroundColor: '#0f172a', padding: '1rem', borderRadius: '6px', fontFamily: 'monospace', fontSize: '0.85rem', color: '#38bdf8' }}>
          $ docker compose -f docker-compose.app.yml up -d
        </div>
        <ul style={{ marginTop: '1.5rem', paddingLeft: '1.5rem', fontSize: '0.9rem', color: '#cbd5e1', lineHeight: 1.8 }}>
          <li><strong>Container 1 (App):</strong> Houses both the compiled Go Backend (port 8088) and the static React Frontend (served via Go static handler).</li>
          <li><strong>Container 2 (Database):</strong> Runs PostgreSQL with a persistent volume mapping to ensure data survives container restarts.</li>
          <li><strong>Container 3 (DWG-Converter):</strong> A dedicated Node.js service for heavy file parsing, communicating internally with the Go backend over the Docker bridge network.</li>
        </ul>
      </div>

    </div>
  );
}
